/**
 * Collection Offers — theme mirror of the `collection-percent-discount`
 * Shopify Function (db-functions/extensions/collection-percent-discount).
 *
 * The function takes a configurable percentage off each qualifying item's
 * FULL price (compare-at if on sale, else current price) once the cart holds
 * at least N qualifying items from a merchant-chosen collection. Lines already
 * discounted at or below that target keep their deeper discount but still
 * count toward the threshold. Wholesale customers and gift cards are excluded.
 *
 * Its config metafield is not readable from the storefront, so the offer
 * (collection, percentage, minimum) is mirrored in theme settings
 * ("Collection Offers"), emitted by layout/theme.liquid as
 * window.theme.settings.collectionOffers, and MUST be kept in sync with the
 * discount configured in Shopify admin.
 *
 * Cart-line eligibility (collection membership, compare-at price) is stamped
 * server-side by snippets/collection-offer-state-json.liquid, because the
 * /cart.js endpoint exposes neither. That JSON lives in the cart drawer
 * (refreshed with every cart merge) and, as a fallback, in the static
 * sections/collection-offer-state.liquid section.
 *
 * Surfaces driven from here:
 *   <collection-offer-message>  PDP block in sections/main-product.liquid
 *   <collection-offer-products> PDP section sections/collection-offer-products.liquid
 *   <collection-offer-upsell>   cart drawer region in sections/cart-drawer.liquid
 *
 * The pure calculation (computeState / unitSaving / messageFor) is exposed on
 * window.theme.collectionOffers so tests/collection-offer.test.js can load
 * this file in a vm sandbox with no DOM.
 */

(function () {
  'use strict';

  const root = typeof window !== 'undefined' ? window : globalThis;
  const theme = (root.theme = root.theme || {});
  theme.settings = theme.settings || {};

  const CO = (theme.collectionOffers = theme.collectionOffers || {});

  // ---------------------------------------------------------------------------
  // Pure logic — mirrors src/run.js in the function. No DOM below this line
  // until the "Runtime" section.
  // ---------------------------------------------------------------------------

  /**
   * Per-unit saving the function would apply to one unit at `priceCents`
   * with the given compare-at, at `percentage` off the full price.
   * Full price = max(price, compareAt). Target = full × (100 − p) / 100.
   * If the unit is already at or below target (deeper sale) the function
   * leaves it alone, so the saving is 0.
   *
   * The arithmetic is the function's own, step for step (run.js at
   * db-functions@bc70435, lines 124-158): it works in currency units and
   * rounds each unit's discount with `toFixed(2)`. Working in cents and
   * rounding once gives a different cent at some prices (1.15 at 30% saves
   * 0.34 at checkout, not 0.35), so the conversion happens only at the ends.
   * A non-finite percentage counts as 0 and a percentage above 100 as 100,
   * as in the function (run.js:206-210).
   *
   * @param {number} priceCents
   * @param {number} compareAtCents - 0 or null when not on sale
   * @param {number} percentage - 0–100
   * @returns {number} cents
   */
  CO.unitSaving = function unitSaving(priceCents, compareAtCents, percentage) {
    const priceC = Number(priceCents) || 0;
    const compareC = Number(compareAtCents) || 0;
    let pct = Number(percentage);
    if (!Number.isFinite(pct)) pct = 0;
    pct = Math.min(Math.max(pct, 0), 100);
    if (priceC <= 0 || pct <= 0) return 0;
    const current = priceC / 100;
    const compareAt = compareC > 0 ? compareC / 100 : 0;
    const full = Math.max(current, compareAt);
    const target = full * ((100 - pct) / 100);
    if (current <= target) return 0;
    return Math.round(Number((current - target).toFixed(2)) * 100);
  };

  const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);
  const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

  /**
   * Validates the cart state emitted by snippets/collection-offer-state-json
   * and returns it normalised, or null if any field that computeState reads
   * is missing or malformed. An empty cart ({"lines": []}) is valid.
   * `giftCard` may be null: the snippet renders `item.product.gift_card`,
   * which is nil for ordinary products, and computeState treats it as falsy.
   *
   * @param {*} parsed - JSON.parse output
   * @returns {{wholesale: boolean, lines: Array}|null}
   */
  CO.normaliseState = function normaliseState(parsed) {
    if (!isPlainObject(parsed)) return null;
    if (parsed.wholesale !== undefined && typeof parsed.wholesale !== 'boolean') return null;
    if (!Array.isArray(parsed.lines)) return null;
    const valid = parsed.lines.every((line) => isPlainObject(line)
      && isFiniteNumber(line.slot)
      && isFiniteNumber(line.quantity) && line.quantity >= 0
      && isFiniteNumber(line.price) && line.price >= 0
      && (line.compareAt === undefined || line.compareAt === null
        || (isFiniteNumber(line.compareAt) && line.compareAt >= 0))
      && (line.giftCard === undefined || line.giftCard === null || typeof line.giftCard === 'boolean'));
    if (!valid) return null;
    return { wholesale: parsed.wholesale === true, lines: parsed.lines };
  };

  /**
   * Aggregate the cart against every configured offer.
   *
   * @param {Array<{slot:number, percentage:number, min:number}>} offers
   * @param {Array<{slot:number, quantity:number, price:number, compareAt:number, giftCard?:boolean}>} lines
   *   Cart lines already tagged with the offer slot they belong to (0 = none).
   * @returns {Object<number, {qty:number, qualified:boolean, remaining:number, existingSaving:number}>}
   *   Keyed by slot. `existingSaving` is what the lines already in the cart
   *   would save once the threshold is met (0 if they are all deeper-discounted).
   */
  CO.computeState = function computeState(offers, lines) {
    const bySlot = {};
    (offers || []).forEach((offer) => {
      const slot = Number(offer.slot);
      const min = Math.max(1, Math.floor(Number(offer.min) || 2));
      let qty = 0;
      let existingSaving = 0;
      (lines || []).forEach((line) => {
        if (Number(line.slot) !== slot) return;
        if (line.giftCard) return;
        const q = Math.max(0, Math.floor(Number(line.quantity) || 0));
        qty += q;
        existingSaving += CO.unitSaving(line.price, line.compareAt, offer.percentage) * q;
      });
      bySlot[slot] = {
        qty,
        min,
        qualified: qty >= min,
        remaining: Math.max(0, min - qty),
        existingSaving
      };
    });
    return bySlot;
  };

  /**
   * Fill `[placeholder]` tokens in a message template.
   * @param {string} template
   * @param {Object<string, string|number>} vars
   * @returns {string}
   */
  CO.render = function render(template, vars) {
    return String(template || '').replace(/\[(\w+)\]/g, (m, key) => (
      Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : ''
    ));
  };

  /**
   * Decide which message a product surface should show for one offer.
   *
   * States:
   *   'default'   — nothing (or not enough) qualifying in the cart and adding
   *                 this item would NOT yet meet the threshold; show the offer.
   *   'unlock'    — adding this one item meets the threshold: saving is this
   *                 unit's saving PLUS what the lines already in the cart save.
   *   'qualified' — threshold already met: adding this item saves its own
   *                 unit saving.
   *   'deeper'    — the item is already discounted at or below the offer's
   *                 target price, so the function will not touch it. It still
   *                 counts toward the threshold.
   *
   * @param {{percentage:number, min:number, title:string}} offer
   * @param {{qty:number, qualified:boolean, remaining:number, existingSaving:number}} slotState
   * @param {{price:number, compareAt:number}} unit - the product/variant being viewed
   * @returns {{state:string, saving:number, remaining:number}}
   */
  CO.messageFor = function messageFor(offer, slotState, unit) {
    const s = slotState || { qty: 0, qualified: false, remaining: Number(offer.min) || 2, existingSaving: 0 };
    const own = CO.unitSaving(unit.price, unit.compareAt, offer.percentage);
    if (own === 0) {
      return { state: 'deeper', saving: 0, remaining: s.remaining };
    }
    if (s.qualified) {
      return { state: 'qualified', saving: own, remaining: 0 };
    }
    if (s.remaining === 1) {
      return { state: 'unlock', saving: own + s.existingSaving, remaining: 1 };
    }
    return { state: 'default', saving: own, remaining: s.remaining };
  };

  /**
   * Pick the offer the cart drawer should promote: the un-met offer with the
   * fewest items still needed (ties → lowest slot), else the first met offer
   * so the buyer can keep adding at the discount. Only offers that already
   * have at least one qualifying line are considered.
   *
   * @param {Array<{slot:number}>} offers
   * @param {Object<number, {qty:number, qualified:boolean, remaining:number}>} bySlot
   * @returns {number} slot, or 0 when nothing in the cart belongs to an offer
   */
  CO.pickUpsellSlot = function pickUpsellSlot(offers, bySlot) {
    let best = 0;
    let bestRemaining = Infinity;
    let firstQualified = 0;
    (offers || []).forEach((offer) => {
      const slot = Number(offer.slot);
      const st = bySlot[slot];
      if (!st || st.qty === 0) return;
      if (st.qualified) {
        if (!firstQualified) firstQualified = slot;
        return;
      }
      if (st.remaining < bestRemaining) {
        bestRemaining = st.remaining;
        best = slot;
      }
    });
    return best || firstQualified;
  };

  // ---------------------------------------------------------------------------
  // Runtime — everything below needs a DOM.
  // ---------------------------------------------------------------------------

  if (typeof document === 'undefined') return;

  const config = () => theme.settings.collectionOffers || { offers: [] };

  const offerBySlot = (slot) => (config().offers || []).find((o) => Number(o.slot) === Number(slot)) || null;

  const money = (cents) => (typeof theme.formatMoney === 'function'
    ? theme.formatMoney(cents)
    : `$${(cents / 100).toFixed(2)}`);

  const STATE_SELECTOR = '[data-collection-offer-state]';
  const STATE_SECTION_URL = () => `${(root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/'}?section_id=collection-offer-state`;

  let cartState = { wholesale: false, lines: [] };
  let bySlot = CO.computeState(config().offers, []);
  let stateLoaded = false;

  // Reads are stamped in the order they are made (an island read when it
  // happens, a fetch when it is sent), and a result is published only if its
  // stamp is newer than the state on show. A slow older read can therefore
  // never replace a newer one. This is local request order, not proof of
  // when the server took its snapshot.
  let clock = 0;
  let publishedStamp = 0;

  /**
   * Parse a state JSON element. Returns null for invalid JSON or a payload
   * of the wrong shape, so a bad render can never be mistaken for a cart.
   */
  function parseStateEl(el) {
    try {
      const state = CO.normaliseState(JSON.parse(el.textContent || ''));
      if (!state) {
        // eslint-disable-next-line no-console
        console.warn('[Collection Offer] Cart state has an unexpected shape; keeping the state on show');
      }
      return state;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Collection Offer] Could not parse cart state', e);
      return null;
    }
  }

  function broadcast() {
    document.dispatchEvent(new CustomEvent('on:collection-offer:state', {
      bubbles: true,
      detail: { state: cartState, bySlot }
    }));
  }

  /**
   * Publish a read's result. The replacement state is computed before either
   * state object is assigned, so a failure can never leave them half replaced.
   * A failed read keeps whatever is on show; only when nothing has loaded yet
   * does it publish the empty cart (once), so the surfaces render their
   * default message, and a later valid read still replaces it. Until the
   * first successful publication the state objects are still the empty
   * cart they were initialised with, so that bootstrap reuses them rather
   * than computing again (a computation that has just failed).
   *
   * @param {number} stamp - when the read was made
   * @param {{wholesale: boolean, lines: Array}|null} next - null on failure
   */
  function publish(stamp, next) {
    let nextBySlot = null;
    if (next) {
      if (stamp <= publishedStamp) return;
      try {
        nextBySlot = CO.computeState(config().offers, next.lines);
      } catch (e) {
        nextBySlot = null;
      }
    }
    if (nextBySlot) {
      cartState = next;
      bySlot = nextBySlot;
      publishedStamp = stamp;
      stateLoaded = true;
      broadcast();
      return;
    }
    if (stateLoaded) return;
    stateLoaded = true;
    broadcast();
  }

  /**
   * Read the state stamped in the cart drawer. Falls back to fetching the
   * static section when there is no drawer on the page (cart page, or the
   * theme set to a cart page instead of a drawer). Never throws or rejects;
   * resolves the published bySlot.
   */
  function refreshState() {
    let el = null;
    try {
      el = document.querySelector(STATE_SELECTOR);
      if (el) {
        // Synchronous: the drawer upsell reads the state while it is being
        // merged in (see CollectionOfferUpsell.connectedCallback).
        publish(++clock, parseStateEl(el));
      }
    } catch (e) {
      // keep the state on show
    }
    if (el) return Promise.resolve(bySlot);
    const stamp = ++clock;
    const failed = () => {
      try { publish(stamp, null); } catch (e) { /* keep the state on show */ }
      return bySlot;
    };
    let request;
    try {
      // A fetch wrapper can throw before returning a promise.
      request = Promise.resolve(fetch(STATE_SECTION_URL()));
    } catch (e) {
      return Promise.resolve(failed());
    }
    return request
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const frag = document.createElement('div');
        frag.innerHTML = html;
        const remote = frag.querySelector(STATE_SELECTOR);
        publish(stamp, remote ? parseStateEl(remote) : null);
        return bySlot;
      })
      .catch(failed);
  }

  CO.getState = () => ({ cart: cartState, bySlot, loaded: stateLoaded });
  CO.refresh = refreshState;

  document.addEventListener('on:cart:after-merge', () => { refreshState(); });
  // Some add paths (quick buy, the cart page) update the cart without a merge
  // on this page; the drawer state may not be present, so re-fetch on the
  // generic change events too.
  document.addEventListener('on:cart:add', () => { setTimeout(refreshState, 50); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { refreshState(); });
  } else {
    refreshState();
  }

  // ---------------------------------------------------------------------------
  // Add-to-cart shared by the section and the drawer cards. Mirrors the
  // shoppable-products flow: post with `sections` so the header and drawer
  // re-render (which also refreshes the offer state), then open the drawer
  // if the theme is set to.
  // ---------------------------------------------------------------------------

  async function addVariantToCart(variantId, quantity) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(quantity || 1));

    const sectionsToUpdate = ['page-header', 'cart-drawer']
      .map((sel) => document.querySelector(sel))
      .filter((el) => el && el.dataset && el.dataset.sectionId);
    const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);
    formData.append('sections_url', root.location.pathname);
    if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

    const response = await fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || data.message || 'Error adding to cart');
    }

    if (data.sections) {
      sectionsToUpdate.forEach((el) => {
        if (el.updateFromCartChange && data.sections[el.dataset.sectionId]) {
          el.updateFromCartChange(data.sections[el.dataset.sectionId]);
        }
      });
    } else {
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    }

    // Open the drawer unless it is already open (adding from a drawer card).
    // `on:cart:add` is deliberately NOT dispatched here: custom.js documents
    // it as "variant added that was not in the cart before", and the sections
    // update above already fires on:cart:after-merge, which refreshes state.
    if (theme.settings.afterAddToCart === 'drawer') {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || !drawer.hasAttribute('open')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      }
    }
    return data;
  }

  // ---------------------------------------------------------------------------
  // Grid host: card interactions (size select, add) shared by the PDP section
  // and the drawer upsell. Cards are rendered by
  // snippets/collection-offer-card.liquid inside sections/collection-offer-grid.liquid.
  // ---------------------------------------------------------------------------

  const GRID_SECTION = 'collection-offer-grid';

  function gridUrl(collectionHandle, tag, page) {
    const base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    let url = `${base}collections/${encodeURIComponent(collectionHandle)}`;
    if (tag) url += `/${encodeURIComponent(tag)}`;
    url += `?section_id=${GRID_SECTION}`;
    if (page && page > 1) url += `&page=${page}`;
    return url;
  }

  /**
   * Fetch one page of the grid section and return its card wrapper element
   * (or null). The wrapper carries data-has-next / data-page.
   */
  async function fetchGrid(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = await response.text();
    const holder = document.createElement('div');
    holder.innerHTML = html;
    return holder.querySelector('[data-collection-offer-grid]');
  }

  /**
   * Write a headline into a message element that carries an icon: the text
   * goes into [data-offer-message-text] when present so the icon survives.
   */
  function setMessage(el, text) {
    const target = el.querySelector('[data-offer-message-text]') || el;
    target.innerHTML = text || '';
    el.hidden = !text;
  }

  /**
   * Horizontal scroller behaviour for a card track: prev/next arrows (shown
   * by CSS only when the track overflows), snap scrolling by ~one viewport,
   * and an "end approaching" callback for loading the next page.
   *
   * @param {HTMLElement} scroller - the [data-scroller] wrapper
   * @param {{onNearEnd?: Function}} hooks
   */
  function setupScroller(scroller, hooks) {
    if (!scroller || scroller.dataset.scrollerReady) return null;
    scroller.dataset.scrollerReady = '1';
    const track = scroller.querySelector('[data-grid]');
    const prev = scroller.querySelector('[data-nav-prev]');
    const next = scroller.querySelector('[data-nav-next]');
    if (!track) return null;

    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const hasOverflow = maxScroll > 1;
      scroller.classList.toggle('has-overflow', hasOverflow);
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
      if (hooks && hooks.onNearEnd && hasOverflow
        && track.scrollLeft + track.clientWidth >= track.scrollWidth - track.clientWidth * 0.6) {
        hooks.onNearEnd();
      }
    };
    const scrollBy = (dir) => track.scrollBy({ left: track.clientWidth * 0.8 * dir, behavior: 'smooth' });

    if (prev) prev.addEventListener('click', () => scrollBy(-1));
    if (next) next.addEventListener('click', () => scrollBy(1));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('on:debounced-resize', update);
    // Images decode after insertion and change the track width.
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(update).observe(track);
    update();
    return { update };
  }

  /**
   * Base class: binds delegated card events once per instance and exposes
   * helpers to (re)compute per-card savings from the current state.
   */
  class CollectionOfferGridHost extends HTMLElement {
    bindCardEvents() {
      if (this.cardEventsBound) return;
      this.cardEventsBound = true;

      this.addEventListener('click', (evt) => {
        const sizeBtn = evt.target.closest('[data-size-variant]');
        if (sizeBtn && this.contains(sizeBtn)) {
          evt.preventDefault();
          this.selectSize(sizeBtn);
          return;
        }
        const addBtn = evt.target.closest('[data-card-add]');
        if (addBtn && this.contains(addBtn)) {
          evt.preventDefault();
          this.addFromCard(addBtn.closest('[data-collection-offer-card]'));
        }
      });
    }

    selectSize(btn) {
      if (btn.disabled) return;
      const card = btn.closest('[data-collection-offer-card]');
      card.querySelectorAll('[data-size-variant]').forEach((b) => {
        b.classList.toggle('is-selected', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      card.dataset.selectedVariant = btn.dataset.sizeVariant;
      card.dataset.price = btn.dataset.price;
      card.dataset.compareAt = btn.dataset.compareAt || '0';
      const add = card.querySelector('[data-card-add]');
      if (add) {
        add.disabled = false;
        add.textContent = add.dataset.addText || 'Add to cart';
      }
      this.updateCardSaving(card);
    }

    async addFromCard(card) {
      if (!card) return;
      const variantId = card.dataset.selectedVariant;
      const add = card.querySelector('[data-card-add]');
      if (!variantId) {
        if (add) add.textContent = add.dataset.selectSizeText || 'Select a size';
        return;
      }
      if (add) {
        add.disabled = true;
        add.classList.add('is-loading');
      }
      try {
        await addVariantToCart(variantId, 1);
        card.classList.add('is-added');
        if (add) add.textContent = add.dataset.addedText || 'Added';
        this.dispatchEvent(new CustomEvent('on:collection-offer:added', {
          bubbles: true,
          detail: { variantId, productId: card.dataset.productId }
        }));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Collection Offer] Add to cart failed', error);
        if (theme.showQuickPopup && add) theme.showQuickPopup(error.message, add);
        document.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: error.message } }));
        if (add) add.textContent = add.dataset.addText || 'Add to cart';
      } finally {
        if (add) {
          add.classList.remove('is-loading');
          add.disabled = false;
        }
      }
    }

    /**
     * Per-card "Save $X" line. Uses the selected variant's price when a size
     * has been chosen, else the product's from-price.
     */
    updateCardSaving(card) {
      const offer = offerBySlot(this.dataset.slot);
      const target = card.querySelector('[data-card-saving]');
      if (!offer || !target) return;
      const st = bySlot[offer.slot];
      const unit = { price: Number(card.dataset.price) || 0, compareAt: Number(card.dataset.compareAt) || 0 };
      const msg = CO.messageFor(offer, st, unit);
      let text = '';
      if (msg.state === 'unlock') {
        text = CO.render(this.dataset.cardUnlockLabel || 'Add to unlock and save [saving]', { saving: money(msg.saving), percentage: offer.percentage });
      } else if (msg.state === 'qualified') {
        text = CO.render(this.dataset.cardSavingLabel || 'Save [saving] at checkout', { saving: money(msg.saving), percentage: offer.percentage });
      } else if (msg.state === 'default') {
        text = CO.render(this.dataset.cardDefaultLabel || 'Save [saving] once unlocked', { saving: money(msg.saving), percentage: offer.percentage });
      } else {
        text = this.dataset.cardDeeperLabel || 'Already reduced — still counts toward the offer';
      }
      target.textContent = text;
      target.hidden = !text;
      card.dataset.offerState = msg.state;
    }

    updateAllCardSavings() {
      this.querySelectorAll('[data-collection-offer-card]').forEach((card) => this.updateCardSaving(card));
    }

    /**
     * Header message for the offer as a whole (no product context).
     */
    offerHeadline() {
      const offer = offerBySlot(this.dataset.slot);
      if (!offer) return '';
      const st = bySlot[offer.slot] || { qty: 0, qualified: false, remaining: offer.min };
      const vars = {
        title: offer.title,
        percentage: offer.percentage,
        min: offer.min,
        remaining: st.remaining,
        qty: st.qty
      };
      if (st.qualified) return CO.render(this.dataset.messageQualified, vars);
      if (st.remaining === 1) return CO.render(this.dataset.messageUnlock, vars);
      return CO.render(this.dataset.messageProgress, vars);
    }
  }

  // ---------------------------------------------------------------------------
  // <collection-offer-message> — PDP block
  // ---------------------------------------------------------------------------

  class CollectionOfferMessage extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.textEl = this.querySelector('[data-text]');
      this.subEl = this.querySelector('[data-subtext]');

      this.onState = () => this.update();
      document.addEventListener('on:collection-offer:state', this.onState);

      // Variant price changes the saving. The event carries the Shopify
      // variant JSON, where prices are in cents.
      this.onVariant = (evt) => {
        const form = evt.detail && evt.detail.form;
        const variant = evt.detail && evt.detail.variant;
        if (!variant) return;
        // Only react to the product form this block sits in: the quickbuy
        // panel renders its own copy of the block with its own form.
        const myForm = this.closest('product-form');
        if (form && myForm && form !== myForm && !myForm.contains(form)) return;
        this.dataset.price = String(variant.price);
        this.dataset.compareAt = String(variant.compare_at_price || 0);
        this.update();
      };
      document.addEventListener('on:variant:change', this.onVariant);

      if (stateLoaded) this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('on:collection-offer:state', this.onState);
      document.removeEventListener('on:variant:change', this.onVariant);
      this.bound = false;
    }

    update() {
      const offer = offerBySlot(this.dataset.slot);
      if (!offer || cartState.wholesale) {
        this.hidden = true;
        return;
      }
      const st = bySlot[offer.slot];
      const unit = { price: Number(this.dataset.price) || 0, compareAt: Number(this.dataset.compareAt) || 0 };
      const msg = CO.messageFor(offer, st, unit);
      const vars = {
        title: offer.title,
        percentage: offer.percentage,
        min: offer.min,
        saving: money(msg.saving),
        remaining: msg.remaining
      };
      const templates = {
        default: this.dataset.templateDefault,
        progress: this.dataset.templateProgress,
        unlock: this.dataset.templateUnlock,
        qualified: this.dataset.templateQualified,
        deeper: this.dataset.templateDeeper
      };

      let text;
      let sub = '';
      if (msg.state === 'unlock') {
        text = CO.render(templates.unlock, vars);
      } else if (msg.state === 'qualified') {
        text = CO.render(templates.qualified, vars);
      } else if (msg.state === 'deeper') {
        text = CO.render(templates.default, vars);
        sub = CO.render(templates.deeper, vars);
      } else {
        text = CO.render(templates.default, vars);
        // Only nudge with a count once something from the offer is in the cart.
        if (st && st.qty > 0 && templates.progress) sub = CO.render(templates.progress, vars);
      }

      this.hidden = !text;
      this.dataset.offerState = msg.state;
      if (this.textEl) this.textEl.innerHTML = text;
      if (this.subEl) {
        this.subEl.innerHTML = sub;
        this.subEl.hidden = !sub;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // <collection-offer-products> — PDP section with three tabs
  // ---------------------------------------------------------------------------

  class CollectionOfferProducts extends CollectionOfferGridHost {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.tabs = Array.from(this.querySelectorAll('[data-offer-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-offer-panel]'));
      this.messageEl = this.querySelector('[data-offer-message]');
      this.currentProductId = this.dataset.currentProductId;

      this.bindCardEvents();

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.offerTab));
        tab.addEventListener('keydown', (evt) => {
          if (evt.key !== 'ArrowRight' && evt.key !== 'ArrowLeft') return;
          const visible = this.tabs.filter((t) => !t.hidden);
          const i = visible.indexOf(tab);
          const next = evt.key === 'ArrowRight' ? visible[(i + 1) % visible.length] : visible[(i - 1 + visible.length) % visible.length];
          if (next) { next.focus(); this.activate(next.dataset.offerTab); }
        });
      });

      this.panels.forEach((panel) => {
        panel.scroller = setupScroller(panel.querySelector('[data-scroller]'), {
          onNearEnd: () => {
            if (panel.dataset.hasNext === 'true' && !panel.dataset.loading) this.loadPanel(panel, true);
          }
        });
      });

      this.onState = () => {
        this.updateMessage();
        this.updateAllCardSavings();
      };
      document.addEventListener('on:collection-offer:state', this.onState);

      const start = () => {
        this.updateMessage();
        const first = this.tabs.find((t) => !t.hidden);
        if (first) this.activate(first.dataset.offerTab);
      };
      if (root.initLazyScript) root.initLazyScript(this, start); else start();
    }

    disconnectedCallback() {
      document.removeEventListener('on:collection-offer:state', this.onState);
      this.initialised = false;
    }

    updateMessage() {
      if (!this.messageEl) return;
      if (cartState.wholesale) { this.hidden = true; return; }
      setMessage(this.messageEl, this.offerHeadline());
    }

    activate(key) {
      this.tabs.forEach((tab) => {
        const on = tab.dataset.offerTab === key;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        const on = panel.dataset.offerPanel === key;
        panel.hidden = !on;
        if (!on) return;
        // Every tab opens at the start of its track, however far the buyer
        // had scrolled it before switching away.
        const track = panel.querySelector('[data-grid]');
        if (track) track.scrollLeft = 0;
        if (panel.scroller) panel.scroller.update();
        if (!panel.dataset.loaded && !panel.dataset.loading) this.loadPanel(panel, false);
      });
    }

    /**
     * Load a page of cards into a tab panel.
     *
     * A panel may carry two sources: `data-priority-tag` (e.g. the other
     * category AND the viewed product's colour) and `data-tag` (the whole
     * category). Phase "priority" pages through the first until it is
     * exhausted, then phase "main" pages through the second, skipping any
     * product already shown. So the outfit tab opens on matching colours and
     * carries on with everything else, in one continuous track.
     *
     * @param {HTMLElement} panel
     * @param {boolean} nextPage - false to (re)start from the first page
     */
    async loadPanel(panel, nextPage) {
      if (!panel || panel.dataset.loading) return;
      const grid = panel.querySelector('[data-grid]');
      const empty = panel.querySelector('[data-empty]');
      const max = Number(this.dataset.maxProducts) || 0;

      if (!nextPage) {
        panel.dataset.phase = panel.dataset.priorityTag ? 'priority' : 'main';
        panel.dataset.page = '0';
        panel.seenIds = new Set();
        grid.innerHTML = '';
      }
      const phase = panel.dataset.phase;
      const page = (Number(panel.dataset.page) || 0) + 1;
      const tag = phase === 'priority' ? panel.dataset.priorityTag : panel.dataset.tag;
      const seen = panel.seenIds || (panel.seenIds = new Set());
      let followUp = false;

      panel.dataset.loading = '1';
      panel.classList.add('is-loading');
      try {
        const fetched = await fetchGrid(gridUrl(this.dataset.collectionHandle, tag, page));
        const excludePattern = panel.dataset.excludePattern === 'true' ? (this.dataset.currentPattern || '') : '';
        // On a v2 product page the cards' product links join the prefetch
        // accelerator's soft navigation (its a.product-link contract), like
        // collection cards; tagged before insertion so its observer sees them.
        // Classic pages and the drawer upsell keep plain links (Mat, 2026-10-01).
        const softLinks = !!document.querySelector('.section-main-product > product-form.pdp-v2');
        if (fetched) {
          fetched.querySelectorAll('[data-collection-offer-card]').forEach((card) => {
            const id = card.dataset.productId;
            if (id === this.currentProductId || seen.has(id)) return;
            // "Another style" must not repeat the viewed style's other colours.
            if (excludePattern && card.dataset.pattern && card.dataset.pattern === excludePattern) return;
            if (max && grid.querySelectorAll('[data-collection-offer-card]').length >= max) return;
            if (softLinks) card.querySelectorAll('a[href*="/products/"]').forEach((a) => a.classList.add('product-link'));
            grid.appendChild(card);
            seen.add(id);
          });
        }
        panel.dataset.page = String(page);
        panel.dataset.loaded = '1';

        let phaseHasNext = !!(fetched && fetched.dataset.hasNext === 'true');
        if (!phaseHasNext && phase === 'priority') {
          // Colour matches exhausted: continue with the rest of the category.
          panel.dataset.phase = 'main';
          panel.dataset.page = '0';
          phaseHasNext = true;
        }
        const total = grid.querySelectorAll('[data-collection-offer-card]').length;
        const hasNext = phaseHasNext && (!max || total < max);
        panel.dataset.hasNext = hasNext ? 'true' : 'false';
        if (empty) empty.hidden = total > 0;

        if (total === 0 && hasNext) {
          // Everything on this page was filtered out; try the next before
          // deciding the tab is empty.
          followUp = true;
        } else if (total === 0) {
          // Nothing to show on this tab (e.g. the only colourway is the one
          // being viewed). Hide the tab and move to the next visible one.
          const tab = this.tabs.find((t) => t.dataset.offerTab === panel.dataset.offerPanel);
          if (tab && this.dataset.hideEmptyTabs !== 'false') {
            tab.hidden = true;
            const nextTab = this.tabs.find((t) => !t.hidden);
            if (nextTab) this.activate(nextTab.dataset.offerTab);
            else this.hidden = true;
          }
        } else if (hasNext && grid.scrollWidth <= grid.clientWidth + 1) {
          // The track is not yet scrollable (a short priority page), so the
          // near-end hook could never fire: fill the first viewport.
          followUp = true;
        }
        this.updateAllCardSavings();
        if (theme.manuallyLoadImages) theme.manuallyLoadImages(this);
        if (panel.scroller) panel.scroller.update();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Collection Offer] Could not load products', error);
        if (empty) { empty.hidden = false; }
      } finally {
        delete panel.dataset.loading;
        panel.classList.remove('is-loading');
        if (followUp && !panel.hidden) this.loadPanel(panel, true);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // <collection-offer-upsell> — cart drawer
  // ---------------------------------------------------------------------------

  class CollectionOfferUpsell extends CollectionOfferGridHost {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.grid = this.querySelector('[data-grid]');
      this.messageEl = this.querySelector('[data-offer-message]');
      this.headingEl = this.querySelector('[data-offer-heading]');
      // Desktop has no swipe and often no horizontal wheel: the arrows are
      // the only way to move the track with a mouse.
      this.scroller = setupScroller(this.querySelector('[data-scroller]'));
      this.bindCardEvents();
      this.onState = () => this.evaluate();
      document.addEventListener('on:collection-offer:state', this.onState);
      // Always re-read the state rather than trusting the cached copy: a cart
      // merge replaces this element synchronously, BEFORE on:cart:after-merge
      // fires, so the cache still describes the previous cart at this point.
      // The state wrapper precedes this one in the drawer, so its JSON has
      // already been merged and refreshState() reads it synchronously.
      refreshState();
    }

    disconnectedCallback() {
      document.removeEventListener('on:collection-offer:state', this.onState);
      this.initialised = false;
      this.setCrossSellsHidden(false);
    }

    setCrossSellsHidden(hide) {
      if (this.dataset.hideCrossSells !== 'true') return;
      const drawer = this.closest('cart-drawer, .cart-drawer') || document;
      drawer.querySelectorAll('.cart-item-upsells').forEach((el) => { el.hidden = hide; });
    }

    evaluate() {
      const slot = cartState.wholesale ? 0 : CO.pickUpsellSlot(config().offers, bySlot);
      const offer = offerBySlot(slot);
      if (!offer) {
        this.hidden = true;
        this.setCrossSellsHidden(false);
        return;
      }
      const changed = this.dataset.slot !== String(slot);
      this.dataset.slot = String(slot);
      this.dataset.collectionHandle = offer.handle;
      if (this.headingEl) this.headingEl.textContent = CO.render(this.dataset.heading || '[title]', { title: offer.title, percentage: offer.percentage, min: offer.min });
      if (this.messageEl) setMessage(this.messageEl, this.offerHeadline());
      this.hidden = false;
      this.setCrossSellsHidden(true);
      if (changed || !this.loaded) this.load();
      else this.updateAllCardSavings();
    }

    async load() {
      if (this.loading) return;
      this.loading = true;
      const max = Number(this.dataset.maxProducts) || 8;
      // The promoted offer can change while the fetch is in flight (another
      // cart change). Render only if it is still the one we fetched for, and
      // fetch again for the new one otherwise.
      const slot = this.dataset.slot;
      try {
        const fetched = await fetchGrid(gridUrl(this.dataset.collectionHandle, '', 1));
        if (slot !== this.dataset.slot) return;
        this.grid.innerHTML = '';
        if (fetched) {
          fetched.querySelectorAll('[data-collection-offer-card]').forEach((card) => {
            if (card.dataset.inCart === 'true') return;
            if (this.grid.querySelectorAll('[data-collection-offer-card]').length >= max) return;
            this.grid.appendChild(card);
          });
        }
        this.loaded = true;
        const total = this.grid.querySelectorAll('[data-collection-offer-card]').length;
        if (total === 0) {
          this.hidden = true;
          this.setCrossSellsHidden(false);
        }
        this.updateAllCardSavings();
        if (theme.manuallyLoadImages) theme.manuallyLoadImages(this);
        if (this.scroller) this.scroller.update();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Collection Offer] Could not load drawer products', error);
        this.hidden = true;
        this.setCrossSellsHidden(false);
      } finally {
        this.loading = false;
        if (slot !== this.dataset.slot && !this.hidden) this.load();
      }
    }
  }

  if (root.customElements) {
    if (!customElements.get('collection-offer-message')) customElements.define('collection-offer-message', CollectionOfferMessage);
    if (!customElements.get('collection-offer-products')) customElements.define('collection-offer-products', CollectionOfferProducts);
    if (!customElements.get('collection-offer-upsell')) customElements.define('collection-offer-upsell', CollectionOfferUpsell);
  }
})();
